import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { DatePipe, DecimalPipe, NgClass, NgIf, NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SidebarModule } from 'primeng/sidebar';
import { TableModule } from 'primeng/table';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EventFound } from '@smatchy/sdk';
import { EventDetailComponent } from '../event-detail/event-detail.component';

/**
 * Whole days between two calendar dates, in local time.
 *
 * Not a millisecond difference. An activity at 20:00 today, read at 10:00, is
 * 0.42 days away, and Math.ceil turns that into 1: the badge said "J−1", the
 * same thing it says for tomorrow. Two different days, one badge.
 *
 * Anchoring both ends to midnight also survives the clock changes, where a day
 * is 23 or 25 hours long and any fixed 86 400 000 divisor drifts.
 */
export function calendarDaysBetween(from: Date, to: Date): number {
  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((startOfDay(to) - startOfDay(from)) / (1000 * 60 * 60 * 24));
}

/** Days-left urgency bands, aligned with the struggling-activities screen. */
const URGENT_DAYS = 3;
const SOON_DAYS = 7;

/** The SDK lags behind the API on a few fields; they show when served. */
type EventWithExtras = EventFound & { price?: number; isRecurring?: boolean };

/**
 * The upcoming activities list, filters first, map on demand.
 *
 * Everything filters client-side: the page already holds every future
 * activity, so sport, city, dates and the participation state are one pass
 * over the array. The detail slides in from the right and the list stays
 * alive behind it.
 */
@Component({
  selector: 'bo-event-list-filter',
  templateUrl: './event-list-filter.component.html',
  styleUrls: ['./event-list-filter.component.scss'],
  imports: [
    TableModule,
    NgClass,
    NgIf,
    NgFor,
    DatePipe,
    DecimalPipe,
    FormsModule,
    SidebarModule,
    GoogleMap,
    MapMarker,
    TranslateModule,
    EventDetailComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventListFilterComponent implements OnChanges {
  @Input() events!: EventFound[];

  private readonly all = signal<EventWithExtras[]>([]);

  readonly search = signal('');
  readonly sport = signal<string | null>(null);
  readonly city = signal('');
  readonly fromDate = signal('');
  readonly toDate = signal('');
  readonly emptyOnly = signal(false);
  readonly paidOnly = signal(false);
  readonly recurringOnly = signal(false);

  readonly mapOpen = signal(false);
  readonly selectedEvent = signal<EventWithExtras | null>(null);

  readonly mapOptions: google.maps.MapOptions = {
    center: { lat: 46.6, lng: 2.4 },
    zoom: 5,
    mapTypeControl: false,
    streetViewControl: false,
    styles: [
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    ],
  };

  /** The sports actually present, so a chip never filters into a dead end. */
  readonly sports = computed(() =>
    [
      ...new Set(
        this.all()
          .map((event) => event.sportTitle)
          .filter(Boolean),
      ),
    ].sort(),
  );

  /** Whether the API serves the optional fields, so their chips only show when real. */
  readonly hasPrice = computed(() => this.all().some((event) => event.price !== undefined));
  readonly hasRecurring = computed(() => this.all().some((event) => event.isRecurring !== undefined));

  readonly filteredEvents = computed(() => {
    const needle = this.search().trim().toLowerCase();
    const city = this.city().trim().toLowerCase();
    const from = this.fromDate() ? new Date(this.fromDate()).getTime() : null;
    const to = this.toDate() ? new Date(this.toDate()).getTime() + 24 * 60 * 60 * 1000 : null;

    return this.all()
      .filter((event) => {
        if (needle && !`${event.title} ${event.sportTitle} ${event.locationAddress}`.toLowerCase().includes(needle)) {
          return false;
        }
        if (this.sport() && event.sportTitle !== this.sport()) {
          return false;
        }
        if (city && !(event.locationAddress ?? '').toLowerCase().includes(city)) {
          return false;
        }
        const start = new Date(event.startDate).getTime();
        if (from !== null && start < from) {
          return false;
        }
        if (to !== null && start >= to) {
          return false;
        }
        // The organiser sits in the attendees, so alone means at most one.
        if (this.emptyOnly() && event.nbAttendees > 1) {
          return false;
        }
        if (this.paidOnly() && !(event.price && event.price > 0)) {
          return false;
        }
        if (this.recurringOnly() && !event.isRecurring) {
          return false;
        }
        return true;
      })
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['events']) {
      this.all.set((this.events ?? []) as EventWithExtras[]);
    }
  }

  toggleSport(sport: string): void {
    this.sport.set(this.sport() === sport ? null : sport);
  }

  #translate = inject(TranslateService);

  daysUntil(event: EventFound): number {
    return Math.max(Math.ceil((new Date(event.startDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)), 0);
  }

  /**
   * Countdown badge that also reads correctly for past activities: "J−N" for
   * something still ahead, "il y a N j" once it has happened, "aujourd'hui"
   * for today, instead of the old clamped "J−0" on everything past.
   */
  dayBadge(event: EventFound): string {
    // Calendar days, not elapsed milliseconds. See calendarDaysBetween.
    //
    // daysUntil above still rounds on milliseconds. It feeds the urgency
    // bands, whose thresholds were chosen against that arithmetic, so
    // changing it would move which activities read as urgent. A separate
    // question from what the badge says, and left alone here.
    const days = calendarDaysBetween(new Date(), new Date(event.startDate));
    if (days >= 1) return this.#translate.instant('eventsList.jMinus', { n: days });
    if (days <= -1) return this.#translate.instant('eventsList.daysAgo', { n: -days });
    return this.#translate.instant('eventsList.today');
  }

  urgency(event: EventFound): 'urgent' | 'soon' | 'later' {
    const days = this.daysUntil(event);
    return days <= URGENT_DAYS ? 'urgent' : days <= SOON_DAYS ? 'soon' : 'later';
  }

  /**
   * Fill counts partner seats, never the organiser: they sit in the
   * attendees and in the capacity, so both counts drop by one. A doubles
   * tennis is 4 seats, one is the organiser, the fill runs over 3.
   */
  partners(event: EventFound): number {
    return Math.max(event.nbAttendees - 1, 0);
  }

  partnerCapacity(event: EventFound): number {
    return Math.max(event.maxNbAttendees - 1, 0);
  }

  fillWidth(event: EventFound): string {
    const capacity = this.partnerCapacity(event);
    if (!capacity) {
      return '0%';
    }
    return `${Math.min((this.partners(event) / capacity) * 100, 100)}%`;
  }

  selectEvent(event: EventWithExtras): void {
    this.selectedEvent.set(event);
  }

  closeDetail(): void {
    this.selectedEvent.set(null);
  }
}
